import { useCombobox, UseComboboxReturnValue } from "downshift"
import { EyeOff, Eye } from "lucide-react"
import { ReactNode, useMemo, useState } from "react"

import { PopoverList, PopoverListItem } from ".."
import { TextButton } from "../../../base/control/TextButton"
import { Label } from "../../../base/informational/Label"
import { FloatingTooltip } from "../../../base/overlay/FloatingTooltip"
import { PopoverTextInput } from "../../../base/overlay/Popover"
import { Icon, IconSizes } from "../../../Icon"
import { filterItems } from "../../combobox/filterItems"

export interface PopoverDualSelectProps<T> {
  items: T[]
  selectedItemKeys: Set<string>
  keyExtractor: (item: T) => string
  valueExtractor: (item: T) => string
  tooltipExtractor?: (item: T) => ReactNode
  renderValue?: (item: T) => ReactNode
  onSelect: (item: T) => void
  onSelectAll: (items: T[]) => void
  onDeselectAll: () => void
  showSelectedFirst?: boolean
  header?: string
  selectedLabel?: string
  deselectedLabel?: string
  selectAllButtonName?: string
  deselectAllButtonName?: string
  showIcons?: boolean
  allowSelectAll?: boolean
  allowDeselectAll?: boolean
  disabled?: boolean
}

export function PopoverDualSelect<T>({
  items,
  selectedItemKeys,
  keyExtractor,
  valueExtractor,
  tooltipExtractor,
  renderValue,
  onSelect,
  onSelectAll,
  onDeselectAll,
  showSelectedFirst = true,
  header = "Select items",
  selectedLabel = "Selected",
  deselectedLabel = "Unselected",
  selectAllButtonName = "Select all",
  deselectAllButtonName = "Remove all",
  showIcons = false,
  allowSelectAll = true,
  allowDeselectAll = true,
  disabled = false,
}: PopoverDualSelectProps<T>) {
  const [searchQuery, setSearchQuery] = useState("")

  const filteredSelectedItems = useMemo(
    () =>
      filterItems({
        items: items.filter((item) => selectedItemKeys.has(keyExtractor(item))),
        filterInputValue: searchQuery,
        valueExtractor,
        autoFilter: true,
      }),
    [items, selectedItemKeys, keyExtractor, searchQuery, valueExtractor]
  )

  const filteredUnselectedItems = useMemo(
    () =>
      filterItems({
        items: items.filter(
          (item) => !selectedItemKeys.has(keyExtractor(item))
        ),
        filterInputValue: searchQuery,
        valueExtractor,
        autoFilter: true,
      }),
    [items, selectedItemKeys, keyExtractor, searchQuery, valueExtractor]
  )

  const dualListItems = useMemo(
    () => [...filteredSelectedItems, ...filteredUnselectedItems],
    [filteredSelectedItems, filteredUnselectedItems]
  )

  const { getMenuProps, highlightedIndex, getItemProps, getInputProps } =
    useCombobox({
      defaultInputValue: "",
      defaultHighlightedIndex: 0,
      items: dualListItems,
      selectedItem: null,
      onSelectedItemChange({ selectedItem }) {
        if (!disabled) {
          onSelect(selectedItem)
        }
      },
      onInputValueChange({ inputValue }) {
        setSearchQuery(inputValue)
      },
      itemToString: (item) => (item ? valueExtractor(item) : ""),
      itemToKey: (item) => (item ? keyExtractor(item) : ""),
      stateReducer: (state, { type, changes }) => {
        const defaultChanges = {
          ...changes,
        }
        switch (type) {
          case useCombobox.stateChangeTypes.InputChange:
            return defaultChanges
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            return {
              ...defaultChanges,
              highlightedIndex: state.highlightedIndex,
              inputValue: state.inputValue,
            }
          default:
            return {
              ...defaultChanges,
              inputValue: state.inputValue,
            }
        }
      },
      isOpen: true,
    })

  const selectedList = (
    <>
      <div className="flex items-center justify-between px-3 py-1">
        <Label containerClassName="color-subtler text-caption">
          {selectedLabel}
        </Label>
        {allowDeselectAll && (
          <TextButton
            size="sm"
            disabled={disabled || filteredSelectedItems.length === 0}
            onClick={onDeselectAll}
          >
            {deselectAllButtonName}
          </TextButton>
        )}
      </div>
      <PopoverDualSelectList
        items={filteredSelectedItems}
        disabled={disabled}
        keyExtractor={keyExtractor}
        valueExtractor={valueExtractor}
        renderValue={renderValue}
        isSelected
        showIcons={showIcons}
        getItemProps={getItemProps}
        highlightedIndex={highlightedIndex}
        indexOffset={0}
        tooltipExtractor={tooltipExtractor}
      />
    </>
  )

  return (
    <div className="flex w-60 flex-1 flex-col">
      <div className="flex items-center justify-between px-3 pt-2">
        <Label>{header}</Label>
      </div>
      <div className="p-2">
        <PopoverTextInput placeholder="Search" {...getInputProps()} />
      </div>
      <div {...getMenuProps()}>
        {showSelectedFirst && selectedList}
        <div className="flex items-center justify-between px-3 py-1">
          <Label containerClassName="color-subtler text-caption">
            {deselectedLabel}
          </Label>
          {allowSelectAll && (
            <TextButton
              size="sm"
              disabled={disabled || filteredUnselectedItems.length === 0}
              onClick={() => onSelectAll(items)}
            >
              {selectAllButtonName}
            </TextButton>
          )}
        </div>
        <PopoverDualSelectList
          disabled={disabled}
          items={filteredUnselectedItems}
          tooltipExtractor={tooltipExtractor}
          keyExtractor={keyExtractor}
          valueExtractor={valueExtractor}
          renderValue={renderValue}
          isSelected={false}
          showIcons={showIcons}
          getItemProps={getItemProps}
          highlightedIndex={highlightedIndex}
          indexOffset={filteredSelectedItems.length}
        />
        {!showSelectedFirst && selectedList}
      </div>
    </div>
  )
}

function PopoverDualSelectList<T>({
  items,
  disabled,
  keyExtractor,
  valueExtractor,
  renderValue,
  isSelected = false,
  showIcons,
  tooltipExtractor,
  getItemProps,
  highlightedIndex,
  indexOffset,
}: {
  items: T[]
  disabled: boolean
  keyExtractor: (item: T) => string
  valueExtractor: (item: T) => string
  renderValue?: (item: T) => ReactNode
  isSelected?: boolean
  showIcons: boolean
  tooltipExtractor?: (item: T) => ReactNode
  getItemProps: UseComboboxReturnValue<T>["getItemProps"]
  highlightedIndex: number
  indexOffset: number
}) {
  return (
    <PopoverList className="max-h-none">
      {items.map((item, index) => {
        const globalIndex = indexOffset + index
        const displayValue = renderValue
          ? renderValue(item)
          : valueExtractor(item)
        const tooltip = tooltipExtractor?.(item)

        return (
          <FloatingTooltip
            key={keyExtractor(item)}
            content={tooltip}
            placement="right-start"
            offsetDistance={8}
            portal={{ renderTo: "document" }}
            isDisabled={!tooltip}
            className="text-body-sm max-w-75"
            trigger={
              <PopoverListItem
                disabled={disabled}
                isHighlighted={globalIndex === highlightedIndex}
                {...getItemProps({ item, index: globalIndex })}
              >
                {showIcons && (
                  <Icon
                    icon={isSelected ? EyeOff : Eye}
                    size={IconSizes.ExtraSmall}
                  />
                )}
                {displayValue}
              </PopoverListItem>
            }
          />
        )
      })}
    </PopoverList>
  )
}
